import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ConditionalOptionValues, InternalFormSubmission } from '@dill/dill-shared';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import { BASE_API_URL } from '../../utils/constants';
import { myFirebaseAuth } from '../../firebase/config';
import { logoutUser } from './authService';

// Like internalSignatureDocumentService.ts, these aren't wired into a slice — each screen keeps
// what it fetched in local state and re-fetches after a mutating call.

type SubmittedInternalFormFieldValue = {
  id: string;
  value: any;
  fieldValue?: ConditionalOptionValues[];
};

const rejectMessage = (error: any) => error?.response?.data?.errors?.[0]?.msg || error?.message;

export const sendInternalForm = createAsyncThunk(
  'internalForms/sendInternalForm',
  async (
    {
      supplierId,
      templateId,
      emails,
      comment,
    }: { supplierId: string; templateId: string; emails: string[]; comment?: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/internalForms/sendInternalForm`,
        { templateId, emails, comment },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);

export const getInternalFormSubmissions = createAsyncThunk(
  'internalForms/getInternalFormSubmissions',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/internalForms/getInternalFormSubmissions`,
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);

export const getInternalFormSubmission = createAsyncThunk(
  'internalForms/getInternalFormSubmission',
  async (
    {
      supplierId,
      internalFormSubmissionId,
    }: { supplierId: string; internalFormSubmissionId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/internalForms/getInternalFormSubmission/${internalFormSubmissionId}`,
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);

// The review page's Internal Notes and Branch & Sales Reps cards; send only what changed.
export const updateInternalFormSubmissionDetails = createAsyncThunk(
  'internalForms/updateInternalFormSubmissionDetails',
  async (
    {
      supplierId,
      internalFormSubmissionId,
      ...details
    }: {
      supplierId: string;
      internalFormSubmissionId: string;
      internalNotes?: InternalFormSubmission['internalNotes'];
      assignedBranchId?: string | null;
      assignedSalesRepIds?: string[];
      ballInCourtId?: string | null;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/internalForms/updateInternalFormSubmissionDetails/${internalFormSubmissionId}`,
        details,
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);

// Re-sends the link to whoever the form is waiting on (first signer or the current later signer).
export const resendInternalFormEmail = createAsyncThunk(
  'internalForms/resendInternalFormEmail',
  async (
    {
      supplierId,
      internalFormSubmissionId,
    }: { supplierId: string; internalFormSubmissionId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/internalForms/resendInternalFormEmail/${internalFormSubmissionId}`,
        {},
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);

// Soft deletes the form: hidden from the Credit Applications table and its link stops working.
export const deleteInternalFormSubmission = createAsyncThunk(
  'internalForms/deleteInternalFormSubmission',
  async (
    {
      supplierId,
      internalFormSubmissionId,
    }: { supplierId: string; internalFormSubmissionId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/internalForms/deleteInternalFormSubmission/${internalFormSubmissionId}`,
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);

// Puts a credit team decision back to "Ready for Decision".
export const revertInternalFormDecision = createAsyncThunk(
  'internalForms/revertInternalFormDecision',
  async (
    {
      supplierId,
      internalFormSubmissionId,
      reason,
    }: { supplierId: string; internalFormSubmissionId: string; reason?: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/internalForms/revertInternalFormDecision/${internalFormSubmissionId}`,
        { reason },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);

export const decideInternalFormSubmission = createAsyncThunk(
  'internalForms/decideInternalFormSubmission',
  async (
    {
      supplierId,
      internalFormSubmissionId,
      decision,
      notes,
      ...approval
    }: {
      supplierId: string;
      internalFormSubmissionId: string;
      decision: 'APPROVED' | 'REJECTED';
      notes?: string;
      customerAction?: 'update' | 'create' | 'none';
      billToCustomerId?: string;
      shipToAccountNumber?: string;
      internalEmails?: string[];
      notifyEmails?: string[];
      message?: string;
      createAsCod?: boolean;
      approvedCreditLimit?: number;
      // Eclipse, creating a Ship-To: its terms.
      terms?: string;
      approvalAdditionalFieldValues?: {
        id: string;
        name: string;
        erpId?: string;
        value?: string;
      }[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/internalForms/decideInternalFormSubmission/${internalFormSubmissionId}`,
        { decision, notes, ...approval },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);

// Unauthenticated — powers the standalone FillInternalForm page reached from the emailed link.
export const getInternalFormForFilling = createAsyncThunk(
  'internalForms/getInternalFormForFilling',
  async (
    {
      internalFormSubmissionId,
      token,
      email,
    }: { internalFormSubmissionId: string; token: string; email: string },
    thunkApi
  ) => {
    try {
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/internalFormsPublic/getInternalFormForFilling/${internalFormSubmissionId}`,
        { headers: {}, params: { token, email } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);

// Unauthenticated counterpart to the above. The IP address is taken server-side.
export const submitInternalForm = createAsyncThunk(
  'internalForms/submitInternalForm',
  async (
    data: {
      internalFormSubmissionId: string;
      token: string;
      email: string;
      signature: string;
      location?: string;
      fieldValues: SubmittedInternalFormFieldValue[];
      name: string;
      title: string;
    },
    thunkApi
  ) => {
    try {
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/internalFormsPublic/submitInternalForm`,
        data,
        { headers: {} }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);

// Unauthenticated — a later signer declining rejects the form.
export const declineInternalForm = createAsyncThunk(
  'internalForms/declineInternalForm',
  async (
    data: { internalFormSubmissionId: string; token: string; email: string; reason: string },
    thunkApi
  ) => {
    try {
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/internalFormsPublic/declineInternalForm`,
        data,
        { headers: {} }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);

// Unauthenticated — live P21 Class 1 codes for a "Marketing Segment" field on FillInternalForm.
export const getP21Class1CodesForInternalForm = createAsyncThunk(
  'internalForms/getP21Class1CodesForInternalForm',
  async (
    {
      internalFormSubmissionId,
      token,
      email,
    }: { internalFormSubmissionId: string; token: string; email: string },
    thunkApi
  ) => {
    try {
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/internalFormsPublic/getP21Class1CodesForInternalForm/${internalFormSubmissionId}`,
        { headers: {}, params: { token, email } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);

// The supplier's INTERNAL_FORM (default) or EXTERNAL_FORM templates. Deliberately not
// getCreditApplicationTemplates: that thunk's result replaces the credit application templates
// held in the store.
export const getInternalFormTemplates = createAsyncThunk(
  'internalForms/getInternalFormTemplates',
  async (
    {
      supplierId,
      applicationType = 'INTERNAL_FORM',
    }: {
      supplierId: string;
      // 'ALL': every template, credit / COD applications included.
      applicationType?: 'INTERNAL_FORM' | 'EXTERNAL_FORM' | 'ALL';
    },
    thunkApi
  ) => {
    try {
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/creditApplications/getCreditApplicationTemplates`,
        { headers: {}, params: { supplierId, applicationType } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);

// Re-runs only the P21 changes whose last attempt failed on an approved internal form.
export const retryInternalFormP21Sync = createAsyncThunk(
  'internalForms/retryInternalFormP21Sync',
  async (
    {
      supplierId,
      internalFormSubmissionId,
    }: { supplierId: string; internalFormSubmissionId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/internalForms/retryInternalFormP21Sync/${internalFormSubmissionId}`,
        {},
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);

// Same as getCreditApplicationPDF: a blob URL to preview, or (preview false) saved straight away.
export const getInternalFormSubmissionPDF = createAsyncThunk(
  'internalForms/getInternalFormSubmissionPDF',
  async (
    {
      supplierId,
      internalFormSubmissionId,
      preview = false,
    }: { supplierId: string; internalFormSubmissionId: string; preview?: boolean },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/internalForms/getInternalFormSubmissionPDF/${internalFormSubmissionId}`,
        { responseType: 'blob', headers: { Authorization: 'Bearer ' + token } }
      );
      const file = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(file);
      if (!preview) {
        link.download = `internal-form-${internalFormSubmissionId}-${+new Date()}.pdf`;
        link.click();
      }
      return { success: true, data: { pdfUrl: link.href } };
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(rejectMessage(error));
    }
  }
);
